import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { History, Loader2, User, FileText, Receipt, Users, Truck, Wallet, Settings, UserPlus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ENTITY_ICONS = {
  invoice: FileText,
  bill: Receipt,
  customer: Users,
  supplier: Truck,
  other_cash: Wallet,
  settings: Settings,
  team_member: UserPlus
};

const ACTION_COLORS = {
  create: 'bg-green-100 text-green-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700'
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, [filter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { entity_type: filter } : {};
      const response = await axios.get(`${API}/audit-log`, { params });
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatChanges = (changes) => {
    if (!changes) return null;
    return Object.entries(changes).map(([key, value]) => (
      <span key={key} className="inline-flex items-center gap-1 text-xs bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded mr-1">
        <span className="text-slate-500">{key}:</span>
        <span className="font-mono">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
      </span>
    ));
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Audit Log
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Track all changes made to your business data
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Filter:</Label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Activities</SelectItem>
                <SelectItem value="invoice">Invoices</SelectItem>
                <SelectItem value="bill">Bills</SelectItem>
                <SelectItem value="customer">Customers</SelectItem>
                <SelectItem value="supplier">Suppliers</SelectItem>
                <SelectItem value="other_cash">Other Cash</SelectItem>
                <SelectItem value="settings">Settings</SelectItem>
                <SelectItem value="team_member">Team</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Activity History
            </CardTitle>
            <CardDescription>
              Recent changes sorted by date (newest first)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No activity recorded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {logs.map((log) => {
                  const Icon = ENTITY_ICONS[log.entity_type] || FileText;
                  return (
                    <div key={log.id} className="flex items-start gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="p-2 bg-white dark:bg-slate-700 rounded-lg shadow-sm">
                        <Icon className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${ACTION_COLORS[log.action]}`}>
                            {log.action.toUpperCase()}
                          </span>
                          <span className="text-sm font-medium capitalize">
                            {log.entity_type.replace('_', ' ')}
                          </span>
                          {log.entity_name && (
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              "{log.entity_name}"
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                          <User className="w-3 h-3" />
                          <span>{log.user_email || 'Unknown user'}</span>
                          <span>•</span>
                          <span>{formatTimestamp(log.timestamp)}</span>
                        </div>
                        {log.changes && (
                          <div className="mt-2">
                            {formatChanges(log.changes)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
